import { useMemo, useState } from 'react';
import { useApp } from '../store';
import {
  authorisedToPay,
  authorisedToPayCsv,
  lineBaseAmount,
  payStampKind,
  payStampRef,
  type AuthorisedLine,
  type FinanceExportRow,
} from '../projects';
import { fmtMoney } from '../money';
import { displayName } from '../people';
import { MONTHS } from '../when';
import Icon from './Icon';

/*
 * THE FINANCE "AUTHORISED TO PAY" QUEUE — Deliverable 3 of invoice-approval
 * governance. Everything approved for payment but not yet paid, across every board,
 * in one read-only list: the invoices finance pays and the proof of who authorised
 * each. No project detail required — finance pays on the authority, and the authority
 * is on the card (and in the stamped page behind "View stamped invoice").
 *
 * Built to the approved mock (dev-preview/invoice-approvals/06-finance-pay-approval-
 * queue.png): one card per invoice — amount, vendor and banking essentials, the
 * pay-authority pill (plain / cover / control-exception), View stamped invoice — with
 * a total and the control-exception count, and an Export for finance (a neutral CSV
 * any finance system can import).
 */

/** A stored timestamp → '27 Sept' — day and month, the way the authority line reads. */
function shortDate(ms?: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
/** 'YYYY-MM-DD' → '30 Sept' for the due date on the card. */
function shortDay(day?: string): string {
  if (!day) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${Number(m[3])} ${mon}` : day;
}

const POOL_WORD = { capex: 'Capex', opex: 'Opex' } as const;
const BASIS_WORD = { approver: 'Designated approver', cover: 'Delegated cover', exception: 'Control exception' } as const;

export default function Payments() {
  const projects = useApp((s) => s.projects);
  const base = useApp((s) => s.currency);
  const fxRates = useApp((s) => s.fxRates);
  const profiles = useApp((s) => s.profiles);
  const authorityToPayUrl = useApp((s) => s.authorityToPayUrl);

  const [busyId, setBusyId] = useState('');
  const [err, setErr] = useState('');

  const { items, exceptionCount } = useMemo(() => authorisedToPay(projects), [projects]);
  const total = useMemo(
    () => items.reduce((sum, it) => sum + lineBaseAmount(it.line, base, fxRates), 0),
    [items, base, fxRates],
  );

  const nameOf = (email?: string) => displayName(email ?? '', profiles);

  /** The authority pill's words + tone, chosen by how the approval was given. */
  const authority = (it: AuthorisedLine) => {
    const { line } = it;
    const kind = payStampKind(line);
    const when = shortDate(line.approvedAt);
    if (kind === 'cover') {
      return { tone: 'cover', icon: 'users' as const, text: `Authorised · ${nameOf(line.approvedBy)} (cover for ${nameOf(line.approvalCoverFor)}) · ${when}` };
    }
    if (kind === 'exception') {
      return { tone: 'exception', icon: 'warning' as const, text: `Authorised · ${nameOf(line.approvedBy)} (admin, in approver's place) · ${when}` };
    }
    return { tone: 'ok', icon: 'shield' as const, text: `Authorised · ${nameOf(line.approvedBy)} · ${when}` };
  };

  /** The second line under the vendor — the banking essentials finance needs. */
  const essentials = (it: AuthorisedLine) => {
    const { line } = it;
    const bits = [
      line.invoiceRef && `${line.invoiceRef}`,
      line.poNumber && line.poNumber,
      line.dueDate && `due ${shortDay(line.dueDate)}`,
      POOL_WORD[line.pool],
    ].filter(Boolean);
    return bits.join(' · ');
  };

  const viewStamped = async (it: AuthorisedLine) => {
    setBusyId(it.line.id);
    setErr('');
    const url = await authorityToPayUrl(it.boardId, it.line.id);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      // The document could not be produced at all — say so rather than letting the
      // click do nothing (interaction-witness F1).
      setErr('Could not open the authority to pay just now. Please try again.');
    }
    setBusyId('');
  };

  const exportForFinance = () => {
    const rows: FinanceExportRow[] = items.map((it) => {
      const { line, boardName } = it;
      const kind = payStampKind(line);
      return {
        board: boardName,
        vendor: line.vendor ?? '',
        amount: Math.round(lineBaseAmount(line, base, fxRates)),
        currency: base,
        invoiceRef: line.invoiceRef ?? '',
        poNumber: line.poNumber ?? '',
        dueDate: line.dueDate ?? '',
        classification: POOL_WORD[line.pool],
        stampRef: payStampRef(line),
        authorisedBy: nameOf(line.approvedBy),
        basis: BASIS_WORD[kind],
        coverFor: kind === 'cover' ? nameOf(line.approvalCoverFor) : '',
        controlException: kind === 'exception' ? (line.approvalReason ?? '') : '',
        invoiceFingerprint: line.invoiceFile?.hash ?? '',
      };
    });
    const blob = new Blob([authorisedToPayCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `authorised-to-pay-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
  };

  return (
    <div className="atp">
      {/* The page header sits OUTSIDE the card — the fd-head pattern, the same
          treatment the Payment approval screen wears — so the two finance screens
          read as one family. The card below holds only the queue. */}
      <div className="fd-head">
        <div className="fd-head-main">
          <h1 className="fd-title">Ready to Pay</h1>
          <p className="fd-sub">
            These invoices have been approved for payment by the designated approver (or their cover).
            Everything finance needs to pay is here — no project detail required.
          </p>
          {err && <p className="sr-note problem">{err}</p>}
        </div>
        {items.length > 0 && (
          <div className="fd-head-side">
            <span className="atp-count">{items.length} {items.length === 1 ? 'invoice' : 'invoices'} · {fmtMoney(Math.round(total))}</span>
            <button className="atp-export" onClick={exportForFinance}>
              <Icon name="download" size={14} /> Export for finance
            </button>
          </div>
        )}
      </div>
      <div className="atp-card">
        {items.length === 0 ? (
          <div className="atp-empty">
            <Icon name="check" size={18} />
            <span>Nothing waiting to be paid. Invoices approved for payment across your boards appear here.</span>
          </div>
        ) : (
          <div className="atp-list">
            {items.map((it) => {
              const a = authority(it);
              return (
                <div className="atp-row" key={`${it.boardId}:${it.line.id}`}>
                  <div className="atp-amt tnum">{fmtMoney(Math.round(lineBaseAmount(it.line, base, fxRates)))}</div>
                  <div className="atp-mid">
                    <div className="atp-vendor">{it.line.vendor || 'Supplier'}</div>
                    <div className="atp-essentials">{essentials(it)}</div>
                    <div className="atp-board">{it.boardName}</div>
                  </div>
                  <div className={`atp-auth atp-${a.tone}`}>
                    <Icon name={a.icon} size={14} />
                    <span>{a.text}</span>
                  </div>
                  <button className="btn btn-small atp-view" disabled={busyId === it.line.id} onClick={() => viewStamped(it)}>
                    <Icon name="document" size={14} /> {busyId === it.line.id ? 'Preparing…' : 'View stamped invoice'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {items.length > 0 && (
          <div className="atp-foot">
            <div className="atp-foot-l">
              <span>Total authorised <b>{fmtMoney(Math.round(total))}</b></span>
              {exceptionCount > 0 && (
                <span className="atp-exc">
                  <Icon name="warning" size={13} /> {exceptionCount} control exception{exceptionCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <span className="atp-foot-r">in {base} (base) · retention held separately</span>
          </div>
        )}
      </div>
    </div>
  );
}
